import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { coachInvitationService } from "../../services/api";

// Confirm modal used for both accept/reject actions
const ConfirmActionModal = ({ action, onCancel, onConfirm, loading }) => {
  const isAccept = action === "accept";
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[24px] w-full max-w-2xl p-10 shadow-2xl">
        <h3 className="text-xl leading-8 font-bold text-[#1A212F] text-center mb-10">
          Are you sure you want to {isAccept ? "accept" : "reject"} this invite?
        </h3>
        <div className="flex items-center justify-center gap-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="py-2 w-1/2 rounded-lg border border-[#E6E6E6] bg-white text-[#1E1E1E] text-sm shadow-sm disabled:opacity-50"
            style={{
              boxShadow: "0px 1px 5px 0px #00000014",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`${
              isAccept
                ? "bg-[#2283FF] hover:brightness-105"
                : "bg-[#FF3B30] hover:brightness-105 "
            } py-2 w-1/2 rounded-lg text-[#F5F5F5] text-sm disabled:opacity-50`}
            style={{
              boxShadow: "0px 1px 5px 0px #00000014",
            }}
          >
            {loading
              ? isAccept
                ? "Accepting..."
                : "Rejecting..."
              : isAccept
              ? "Accept Invite"
              : "Reject Invite"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal listing all invites when more than one is present
const MultipleInvitesModal = ({ invites, onClose, onActionClick }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Pending Coach Invitations
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="flex items-center justify-between px-6 py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg"
              style={{ boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)" }}
            >
              <div className="flex items-center gap-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" rx="6" fill="url(#grad)" />
                  <defs>
                    <linearGradient
                      id="grad"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="text-sm font-medium">
                  You have a coach invitation for your email "{invite.email}".
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onActionClick(invite, "decline")}
                  className="bg-[#FF3B30] px-4 py-2 rounded-xl text-white font-bold"
                >
                  Reject
                </button>
                <button
                  onClick={() => onActionClick(invite, "accept")}
                  className="bg-[#2283FF] px-4 py-2 rounded-xl text-white font-bold"
                >
                  Accept
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CoachPendingInvites = () => {
  const { currentRole } = useSelector((s) => s.auth);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMultipleModal, setShowMultipleModal] = useState(false);
  const [confirm, setConfirm] = useState({
    open: false,
    inviteId: null,
    action: null,
  });
  const [responding, setResponding] = useState(false);

  const isCoach = useMemo(() => currentRole === "Coach", [currentRole]);

  useEffect(() => {
    if (!isCoach) return;
    const fetchInvites = async () => {
      setLoading(true);
      try {
        const res = await coachInvitationService.getPendingCoachInvitations();
        if (res.success) {
          setInvites(res.data || []);
        } else {
          toast.error(res.error || "Failed to fetch coach invites");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInvites();
  }, [isCoach]);

  const handleOpenConfirm = (invite, action) => {
    setConfirm({ open: true, inviteId: invite.id, action });
  };

  const handleConfirm = async () => {
    if (!confirm.inviteId || !confirm.action) return;
    setResponding(true);
    try {
      const res = await coachInvitationService.respondToCoachInvitation(
        confirm.inviteId,
        confirm.action === "accept" ? "accept" : "decline"
      );
      if (res.success) {
        setInvites((prev) => prev.filter((i) => i.id !== confirm.inviteId));
        toast.success(
          confirm.action === "accept"
            ? "Invitation accepted"
            : "Invitation rejected"
        );
      } else {
        toast.error(res.error || "Action failed");
      }
    } catch (e) {
      toast.error("Action failed. Please try again.");
    } finally {
      setResponding(false);
      setConfirm({ open: false, inviteId: null, action: null });
    }
  };

  if (!isCoach || loading || invites.length === 0) return null;

  const shouldShowStacked = invites.length > 1;

  return (
    <div className="mb-4 mt-5">
      {shouldShowStacked && (
        <div className="relative h-20 mt-32 md:-mt-2">
          {invites.slice(0, 3).map((invite, index) => (
            <div
              key={invite.id}
              className="absolute flex flex-col md:flex-row items-start md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg cursor-pointer w-[calc(100%-2rem)] md:w-full"
              style={{
                maxWidth: `${1400 - index * 24}px`,
                height: "auto",
                left: "1rem",
                bottom: `${20 - index * 10}px`,
                zIndex: invites.length - index,
                opacity: index === 0 ? 1 : index === 1 ? 0.7 : 0.5,
                boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
              }}
              onClick={() => setShowMultipleModal(true)}
            >
              <div className="flex items-center gap-2 mb-2 md:mb-0">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="6"
                    fill="url(#paint0_linear_coach)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_coach"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="text-sm font-medium">
                  You have a new coach invitation for "{invite.email}".
                </span>
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenConfirm(invite, "accept");
                  }}
                  className="bg-[#2283FF] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
                >
                  Accept
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenConfirm(invite, "decline");
                  }}
                  className="bg-[#FF2D55] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!shouldShowStacked && invites.length === 1 && (
        <div className="relative h-auto md:h-20 mt-32 md:-mt-2">
          <div
            className="absolute flex flex-col md:flex-row items-start md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg w-[calc(100%-2rem)] md:w-full"
            style={{
              maxWidth: "1400px",
              height: "auto",
              left: "1rem",
              bottom: "20px",
              boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
            }}
          >
            <div className="flex items-center gap-2 mb-2 md:mb-0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="24"
                  height="24"
                  rx="6"
                  fill="url(#paint0_linear_coach_single)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_coach_single"
                    x1="12"
                    y1="0"
                    x2="12"
                    y2="24"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#DBA948" />
                    <stop offset="0.0001" stopColor="#FFC46B" />
                    <stop offset="1" stopColor="#FFA318" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="text-sm font-medium">
                You have a coach invitation for "{invites[0].email}".
              </span>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto">
              <button
                onClick={() => handleOpenConfirm(invites[0], "decline")}
                className="bg-[#FF3B30] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
              >
                Reject
              </button>
              <button
                onClick={() => handleOpenConfirm(invites[0], "accept")}
                className="bg-[#2283FF] px-4 py-2 rounded-xl text-white font-bold w-full md:w-auto"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {showMultipleModal && (
        <MultipleInvitesModal
          invites={invites}
          onClose={() => setShowMultipleModal(false)}
          onActionClick={(invite, action) => handleOpenConfirm(invite, action)}
        />
      )}

      {confirm.open && (
        <ConfirmActionModal
          action={confirm.action === "decline" ? "reject" : "accept"}
          loading={responding}
          onCancel={() =>
            setConfirm({ open: false, inviteId: null, action: null })
          }
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

export default CoachPendingInvites;

